import React from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormHelperText,
  TextField,
  Typography,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { COLORS } from "../assets/theme";
import CustomLabel from "./common/CustomLabel";

export interface GoldMultiSelectOption {
  label: string;
  value: string;
}

export const MONTH_OPTIONS: GoldMultiSelectOption[] = [
  { label: "January", value: "1" },
  { label: "February", value: "2" },
  { label: "March", value: "3" },
  { label: "April", value: "4" },
  { label: "May", value: "5" },
  { label: "June", value: "6" },
  { label: "July", value: "7" },
  { label: "August", value: "8" },
  { label: "September", value: "9" },
  { label: "October", value: "10" },
  { label: "November", value: "11" },
  { label: "December", value: "12" },
];

const goldChipSx = {
  borderRadius: "6px",
  fontSize: "0.7rem",
  height: 22,
  my: 0.25,
  bgcolor: COLORS.goldChipBackground,
  color: COLORS.GOLD,
  border: `1px solid ${COLORS.goldChipBorder}`,
  "& .MuiChip-deleteIcon": {
    color: COLORS.goldChipDeleteIcon,
    "&:hover": { color: COLORS.GOLD },
  },
};

const goldCheckboxSx = {
  mr: 1,
  p: 0.5,
  color: COLORS.GOLD,
  "&.Mui-checked": { color: COLORS.GOLD },
};

export interface GoldMultiSelectFieldProps {
  label: string;
  placeholder: string;
  options: GoldMultiSelectOption[];
  value: GoldMultiSelectOption[];
  onChange: (selected: GoldMultiSelectOption[]) => void;
  error?: boolean;
  helperText?: string;
}

const GoldMultiSelectField: React.FC<GoldMultiSelectFieldProps> = ({
  label,
  placeholder,
  options,
  value,
  onChange,
  error = false,
  helperText = "",
}) => {
  const allSelected = options.length > 0 && value.length === options.length;
  const noneSelected = value.length === 0;

  return (
    <Box sx={{ width: "100%", alignSelf: "stretch" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
          width: "100%",
        }}
      >
        <CustomLabel
          text={label}
          fontWeight={500}
          fontSize="14px"
          highlightLabelStar={true}
          sx={{ margin: 0 }}
        />
        <Box sx={{ display: "flex", gap: 0.75 }}>
          <Button
            size="small"
            variant={allSelected ? "contained" : "outlined"}
            onClick={() => onChange(options)}
            sx={{
              minWidth: "auto",
              px: 1.25,
              py: 0.25,
              fontSize: "0.7rem",
              fontWeight: 600,
              lineHeight: 1.4,
              textTransform: "none",
              borderRadius: "6px",
              boxShadow: "none",
              ...(allSelected
                ? {
                    bgcolor: COLORS.GOLD,
                    color: COLORS.White,
                    "&:hover": { bgcolor: COLORS.GOLD, boxShadow: "none" },
                  }
                : {
                    color: COLORS.GOLD,
                    borderColor: COLORS.GOLD,
                    "&:hover": {
                      borderColor: COLORS.GOLD,
                      bgcolor: COLORS.goldButtonHoverBackground,
                      boxShadow: "none",
                    },
                  }),
            }}
          >
            Select All
          </Button>
          <Button
            size="small"
            variant={noneSelected ? "contained" : "outlined"}
            onClick={() => onChange([])}
            sx={{
              minWidth: "auto",
              px: 1.25,
              py: 0.25,
              fontSize: "0.7rem",
              fontWeight: 600,
              lineHeight: 1.4,
              textTransform: "none",
              borderRadius: "6px",
              boxShadow: "none",
              ...(noneSelected
                ? {
                    bgcolor: COLORS.mutedSurfaceBackground,
                    color: COLORS.defaultGray,
                    border: `1px solid ${COLORS.mutedSurfaceBorder}`,
                    "&:hover": {
                      bgcolor: COLORS.mutedSurfaceBackground,
                      boxShadow: "none",
                    },
                  }
                : {
                    color: COLORS.defaultGray,
                    borderColor: COLORS.inputBorderDefault,
                    "&:hover": {
                      borderColor: COLORS.inputBorderHover,
                      bgcolor: COLORS.inputHoverBackground,
                      boxShadow: "none",
                    },
                  }),
            }}
          >
            Clear
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          width: "100%",
          borderRadius: "10px",
          border: `1px solid ${error ? COLORS.errorRed : COLORS.inputBorderDefault}`,
          backgroundColor: COLORS.WhiteBackground,
          overflow: "hidden",
          position: "relative",
          minHeight: 48,
          maxHeight: 96,
          transition: "border-color 0.2s",
          "&:hover": {
            borderColor: error ? COLORS.errorRed : COLORS.GOLD,
          },
          "&:focus-within": {
            borderColor: error ? COLORS.errorRed : COLORS.GOLD,
          },
        }}
      >
        <Autocomplete
          multiple
          fullWidth
          disableCloseOnSelect
          options={options}
          value={value}
          onChange={(_, newValue) => onChange(newValue)}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, val) =>
            String(option.value) === String(val.value)
          }
          sx={{
            width: "100%",
            "& .MuiOutlinedInput-root": {
              position: "relative",
              borderRadius: "10px",
              fontSize: "0.85rem",
              minHeight: 46,
              maxHeight: 96,
              flexWrap: "wrap",
              alignItems: "flex-start",
              alignContent: "flex-start",
              gap: 0.5,
              overflowY: "auto",
              overflowX: "hidden",
              backgroundColor: "transparent",
              pt: 0.75,
              pb: 1.25,
              pl: 0.75,
              pr: "52px",
              boxSizing: "border-box",
              "& fieldset": { border: "none" },
              "& .MuiAutocomplete-input": {
                minWidth: "30px !important",
                py: 0.25,
              },
            },
          }}
          renderOption={(props, option, { selected }) => (
            <li {...props} key={option.value}>
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                checked={selected}
                sx={goldCheckboxSx}
              />
              <Typography variant="body2">{option.label}</Typography>
            </li>
          )}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.value}
                label={option.label}
                size="small"
                onClick={(e) => e.stopPropagation()}
                sx={{
                  ...goldChipSx,
                  cursor: "default",
                  "& .MuiChip-label": { cursor: "default" },
                }}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              placeholder={value.length === 0 ? placeholder : ""}
              size="small"
              sx={{ width: "100%" }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 10,
                      right: 8,
                      display: "flex",
                      alignItems: "center",
                      height: 24,
                      zIndex: 2,
                      backgroundColor: COLORS.WhiteBackground,
                      pointerEvents: "auto",
                    }}
                  >
                    {params.InputProps.endAdornment}
                  </Box>
                ),
              }}
            />
          )}
          slotProps={{ listbox: { style: { maxHeight: 260 } } }}
        />
      </Box>
      {helperText ? (
        <FormHelperText error={error}>{helperText}</FormHelperText>
      ) : null}
    </Box>
  );
};

export default GoldMultiSelectField;
